import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { 
  HelpCircle, 
  Home, 
  Store, 
  Cog, 
  Package, 
  User, 
  MessageSquare,
  CheckCircle2,
  Play,
  BookOpen,
  Lock,
  Briefcase,
  Wrench,
  ShieldCheck,
  Heart,
  Shield,
  Bell,
  BarChart3
} from 'lucide-react';
import { useTutorialContext, TutorialType } from './TutorialProvider';
import { useAuth } from '@/components/AuthProvider';
import { cn } from '@/lib/utils';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface TutorialItem {
  id: TutorialType;
  label: string;
  icon: React.ReactNode;
  description: string;
  route?: string;
  requiresAuth?: boolean;
}

const tutorials: TutorialItem[] = [
  {
    id: 'home',
    label: 'Homepage Tour',
    icon: <Home className="w-4 h-4" />,
    description: 'Learn the basics',
    route: '/',
  },
  {
    id: 'marketplace',
    label: 'Marketplace Tour',
    icon: <Store className="w-4 h-4" />,
    description: 'Search & filter listings',
    route: '/marketplace',
  },
  {
    id: 'privacy',
    label: 'Privacy & Data Control',
    icon: <Shield className="w-4 h-4" />,
    description: 'Protect your sensitive data',
    route: '/profile?section=security',
    requiresAuth: true,
  },
  {
    id: 'listingManagement',
    label: 'Dashboard Management',
    icon: <BarChart3 className="w-4 h-4" />,
    description: 'Manage & track listings',
    route: '/profile?section=dashboard',
    requiresAuth: true,
  },
  {
    id: 'alerts',
    label: 'Alerts & Notifications',
    icon: <Bell className="w-4 h-4" />,
    description: 'Custom search alerts',
    route: '/profile?section=alerts',
    requiresAuth: true,
  },
  {
    id: 'helicopterWizard',
    label: 'Helicopter Wizard',
    icon: <HelicopterSilhouetteIcon className="w-4 h-4" />,
    description: 'Create helicopter listings',
    route: '/create-listing?type=sale',
  },
  {
    id: 'engineWizard',
    label: 'Engine Wizard',
    icon: <Cog className="w-4 h-4" />,
    description: 'List engines with specs',
    route: '/create-listing-engine?type=engine_sell',
  },
  {
    id: 'partsWizard',
    label: 'Parts Wizard',
    icon: <Package className="w-4 h-4" />,
    description: 'List parts & tools',
    route: '/create-listing-parts?type=parts_sell_general',
  },
  {
    id: 'profile',
    label: 'Profile Navigation',
    icon: <User className="w-4 h-4" />,
    description: 'Navigate your account sections',
    route: '/profile',
    requiresAuth: true,
  },
  {
    id: 'jobWizard',
    label: 'Job Wizard',
    icon: <Briefcase className="w-4 h-4" />,
    description: 'Post jobs & profiles',
    route: '/create-listing-jobs?type=job_post',
  },
  {
    id: 'serviceWizard',
    label: 'Service Wizard',
    icon: <Wrench className="w-4 h-4" />,
    description: 'List aviation services',
    route: '/create-listing-services?type=service_offer',
  },
  {
    id: 'messaging',
    label: 'Messaging Tour',
    icon: <MessageSquare className="w-4 h-4" />,
    description: 'Chat with buyers/sellers',
    route: '/messages',
    requiresAuth: true,
  },
  {
    id: 'verification',
    label: 'Verification Tour',
    icon: <ShieldCheck className="w-4 h-4" />,
    description: 'Get your verified badge',
    route: '/profile?section=verification',
    requiresAuth: true,
  },
  {
    id: 'savedCompare',
    label: 'Saved & Compare',
    icon: <Heart className="w-4 h-4" />,
    description: 'Save & compare listings',
    route: '/profile?section=favorites',
    requiresAuth: true,
  },
];

interface HelpMenuProps {
  isSubmenu?: boolean;
}

export const HelpMenu: React.FC<HelpMenuProps> = ({ isSubmenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Safe context access - return null if not within TutorialProvider
  let tutorialContext: ReturnType<typeof useTutorialContext> | null = null;
  try {
    tutorialContext = useTutorialContext();
  } catch {
    // Context not available - will render without tutorial functionality
  }

  const handleTutorialClick = (tutorial: TutorialItem) => {
    if (!tutorialContext) return;
    
    // Let TutorialProvider handle all navigation internally
    tutorialContext.startTutorial(tutorial.id);
  };

  const completedCount = tutorialContext?.completedTutorials.length ?? 0;
  const totalCount = tutorials.length;

  const content = (
    <>
      <DropdownMenuLabel className="flex items-center justify-between">
        <span>Interactive Tutorials</span>
        <span className="text-xs font-normal text-muted-foreground">
          {completedCount}/{totalCount} completed
        </span>
      </DropdownMenuLabel>
      
      <DropdownMenuSeparator />
      
      {tutorials.map((tutorial) => {
        const isCompleted = tutorialContext?.completedTutorials.includes(tutorial.id) ?? false;
        const isLocked = tutorial.requiresAuth && !user;
        
        return (
          <DropdownMenuItem
            key={tutorial.id}
            onClick={() => handleTutorialClick(tutorial)}
            className={cn(
              "flex items-start gap-3 py-2.5 cursor-pointer",
              isLocked && "opacity-60"
            )}
          >
            <div className={cn(
              "p-1.5 rounded-md",
              isLocked ? "bg-muted text-muted-foreground" :
              isCompleted ? "bg-green-500/10 text-green-500" : "bg-primary/10 text-primary"
            )}>
              {isLocked ? <Lock className="w-4 h-4" /> :
               isCompleted ? <CheckCircle2 className="w-4 h-4" /> : tutorial.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{tutorial.label}</span>
                {isCompleted && (
                  <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
                )}
                {isLocked && (
                  <span className="text-[10px] text-muted-foreground">Sign in</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate">
                {tutorial.description}
              </p>
            </div>
            {!isCompleted && !isLocked && (
              <Play className="w-4 h-4 text-muted-foreground shrink-0 self-center" />
            )}
          </DropdownMenuItem>
        );
      })}
      
      <DropdownMenuSeparator />
      
      <DropdownMenuItem
        onClick={() => navigate('/tutorials')}
        className="flex items-center gap-3 py-2.5 cursor-pointer"
      >
        <div className="p-1.5 rounded-md bg-primary/10 text-primary">
          <BookOpen className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <span className="font-medium text-sm">All Tutorials</span>
          <p className="text-xs text-muted-foreground">
            View complete tutorial library
          </p>
        </div>
      </DropdownMenuItem>
    </>
  );

  if (isSubmenu) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger className="cursor-pointer">
          <HelpCircle className="mr-2 h-4 w-4" />
          <span>Interactive Tutorials</span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-72">
          {content}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Help & Tutorials"
        >
          <HelpCircle className="w-5 h-5" />
          {completedCount > 0 && completedCount < totalCount && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
              {completedCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {content}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
